import React from 'react'
import {
  $DCDangerColor,
  $DCDangerLightColor,
  $DCInfoColor,
  $DCInfoLightColor,
  $DCSuccessColor,
  $DCSuccessLightColor,
  $DCWarningColor,
  $DCWarningLightColor,
  DcTag,
  PixelButton,
  PixelDate,
  PixelDiv,
  PixelFlexBox,
  PixelTable,
  PixelText,
} from 'pixel-base'
import queryString from 'query-string'

import { get } from 'utils/api-routes.util'

interface HistoryItem {
  recipient: string
  template_name?: string
  recipient_number?: string
  message?: string
  status: string
  error?: string
  created_at: { date: string }
  updated_at?: { date: string }
}

interface ApiResponse {
  response: {
    data: HistoryItem[]
    _meta: {
      total: number
    }
  }
}

interface Column {
  dataKey: string
}

const NotificationHistory: React.FC = () => {
  const [history, setHistory] = React.useState<HistoryItem[]>([])
  const [tableLoading, setTableLoading] = React.useState<boolean>(false)
  const p = queryString.parse(window.location.search)
  const [totalRows, setTotalRows] = React.useState<number>(0)
  const [selectedErrorRow, setSelectedErrorRow] = React.useState<number | null>(
    null,
  )
  const statusInfoRefs = React.useRef<Record<number, HTMLDivElement | null>>({})
  const [popupValues, setPopupValues] = React.useState<Record<number, string>>(
    {},
  )
  const [filters, setFilters] = React.useState<{
    search: string | null
    page: string | number
    page_size: string | number
    sort_by: string | null
    sort_order: string
    communication_type: string
  }>({
    search: p.search || null,
    page: p.page || 0,
    page_size: p.page_size || 25,
    sort_by: p.sort_by || null,
    sort_order: p.sort_order || 'DESC',
    communication_type: 'whatsapp',
  })

  const readData = () => {
    setTableLoading(true)
    get('/communication/queue', filters).subscribe((res: ApiResponse) => {
      setTableLoading(false)
      setHistory(res.response.data || [])
      setTotalRows(res.response._meta.total)
    })
  }

  const handlePerRowsChange = async (newPerPage: number) => {
    setFilters({
      ...filters,
      page_size: newPerPage,
    })
  }

  const handlePageChange = (page: number) => {
    if (filters.page === page - 1) return
    setFilters({
      ...filters,
      page: page - 1,
    })
  }

  const handleSort = (column: Column, sortDirection: string) => {
    setFilters({
      ...filters,
      sort_by: column.dataKey,
      sort_order: sortDirection === 'asc' ? 'ASC' : 'DESC',
    })
  }

  const handleStatusClicking = (row: HistoryItem, index: number) => {
    if (row.status === 'failed') {
      setSelectedErrorRow(selectedErrorRow === index ? null : index)
      setPopupValues({
        ...popupValues,
        [index]: row.error || 'No error message available.',
      })
    }
  }

  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        // Check if click is outside all error popups
        const clickedElement = event.target as HTMLElement
        const isClickOnErrorPopup = Object.values(statusInfoRefs.current).some(
          (ref) => ref && ref.contains(clickedElement),
        )
        if (!isClickOnErrorPopup) {
          setSelectedErrorRow(null)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  React.useEffect(() => {
    readData()
  }, [filters])

  const columns = [
    {
      dataKey: 'recipient',
      name: 'Recipient',
      selector: (row: HistoryItem) => row.recipient_number || '--',
    },
    {
      dataKey: 'template_name',
      name: 'Template',
      selector: (row: HistoryItem) =>
        row.template_name ? <PixelText>{row.template_name}</PixelText> : '--',
    },
    {
      name: 'Message',
      dataKey: 'text',
      width: '30%',
      sortable: true,
      selector: (row: HistoryItem) => (
        <PixelText multiLine>{row.message || '--'}</PixelText>
      ),
    },
    {
      dataKey: 'status',
      name: 'Status',
      sortable: true,
      selector: (row: HistoryItem, index: number) => (
        <PixelFlexBox
          onClick={() => handleStatusClicking(row, index)}
          style={{
            cursor: 'pointer',
            borderBottomStyle: row.status === 'failed' ? 'dotted' : 'none',
            borderBottomWidth: row.status === 'failed' ? '1.5px' : '0px',
            borderBottomLeftRadius: row.status === 'failed' ? '10px' : '0px',
            borderBottomRightRadius: row.status === 'failed' ? '10px' : '0px',
            paddingBottom: '1px',
            // borderBottomColor: '#a30bfd',
          }}
        >
          <DcTag
            customBackgroundColor={
              row.status === 'sent'
                ? $DCSuccessLightColor
                : row.status === 'failed'
                ? $DCDangerLightColor
                : row.status === 'queued'
                ? $DCWarningLightColor
                : $DCInfoLightColor
            }
            textColor={
              row.status === 'sent'
                ? $DCSuccessColor
                : row.status === 'failed'
                ? $DCDangerColor
                : row.status === 'queued'
                ? $DCWarningColor
                : $DCInfoColor
            }
          >
            {row.status}
            {selectedErrorRow === index && (
              <PixelDiv
                ref={(ref) => (statusInfoRefs.current[index] = ref)}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '35%',
                  transform: 'translateY(-50%)',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 0 5px rgba(0, 0, 0, 0.2)',
                  borderRadius: '10px',
                  padding: '10px',
                  // color: '#a30bfd',
                  cursor: 'pointer',
                  fontWeight: '500',
                  zIndex: 10,
                }}
              >
                {popupValues[index]}
              </PixelDiv>
            )}
          </DcTag>
        </PixelFlexBox>
      ),
    },

    {
      dataKey: 'created_at',
      name: 'Sent Date',
      selector: (row: HistoryItem) =>
        row.updated_at ? (
          <PixelDate format="pixelStandard" value={row.updated_at.date} />
        ) : (
          '--'
        ),
    },
  ]

  return (
    <>
      <PixelFlexBox width="100%" justifyContent="flex-end">
        <PixelFlexBox
          gap="10px"
          width="30%"
          justifyContent="flex-end"
          padding="10px 0"
        >
          <PixelButton
            variant="secondary"
            onClick={() => {
              readData()
            }}
          >
            Refresh
          </PixelButton>
        </PixelFlexBox>
      </PixelFlexBox>

      <div ref={containerRef}>
        <PixelTable
          columns={columns}
          data={history || []}
          progressPending={tableLoading}
          paginationServer
          paginationTotalRows={totalRows}
          paginationRowsPerPageOptions={[25, 50, 100]}
          onChangeRowsPerPage={handlePerRowsChange}
          paginationDefaultPage={Number(filters.page) + 1}
          onChangePage={handlePageChange}
          onSort={handleSort}
          fixedHeader
          fixedHeaderScrollHeight="calc(100vh - 280px)"
        />
      </div>
    </>
  )
}

export default NotificationHistory
